import { Application, utils, sound } from 'pixi.js'

import ScaleMode from './ScaleMode'
import Device from './Device'
import Layer from './Layer'
import { TWEEN } from '../animation'
import { ECS } from '../ecs'
import { WidgetSystem } from '../systems'
import ResManager from '../res/ResManager'
import SceneManager from '../scene/SceneManager'
import { classname } from '../utils'
import res from 'res'

// keep quiet!
utils.skipHello()

const defaultOptions = {
  width: 750,
  height: 1500,
  transparent: true,
  sharedTicker: false,
  antialias: true,
  autoStart: false,
  scaleMode: 'COVER',
  backgroundColor: 'rgba(1, 1, 1, 1)',
}

class FT {
  #container
  #options
  #engine
  #view
  #renderer
  #stage

  constructor() {
    // ECS subsystem
    const ecs = new ECS()
    ecs.addSystem(new WidgetSystem())
    this.ecs = ecs

    // Resource Manager
    this.res = res
    this.rm = ResManager.default

    // Scene Manager
    this.sm = SceneManager.default
  }

  initialize(selector, options) {
    this.#options = Object.assign(defaultOptions, options)

    const engine = new Application(this.#options)

    this.#engine = engine
    this.#view = engine.view
    this.#renderer = engine.renderer
    this.#stage = engine.stage

    this._bindProperties(engine)

    const container = document.querySelector(selector)
    this.#container = container
    this._checkContainer(container)
    this._styleContainer(container)
    container.appendChild(this.#view)

    window.addEventListener('resize', this._onResize)
    this._onResize()

    this._onVisibilityChange()

    engine.ticker.add(dt => {
      TWEEN.update(performance.now())
      this.ecs.update(dt)
    })
  }

  start() {
    this.#engine.start()
  }

  stop() {
    this.#engine.stop()
  }

  get backgroundColor() {
    return this.#container.style.backgroundColor
  }

  set backgroundColor(color) {
    this.#container.style.backgroundColor = color
  }

  /**
   * Bind instance's properties on FT. It helps me to write less code.
   */
  _bindProperties(app) {
    window.FT = this
    this.instance = this
    this.internal = {}
    this.engine = app
    this.view = app.view
    this.renderer = app.renderer
    this.internal.stage = app.stage
    this.screen = app.screen
    this.ticker = app.ticker
  }

  _checkContainer(container) {
    if (!container) {
      throw new Error(`[${classname(this)}] invalid selector of container`)
    }
  }

  _styleContainer(container) {
    const { backgroundColor } = this.#options
    container.style.backgroundColor = backgroundColor
    container.style.userSelect = 'none'
    container.style.touchAction = 'none'
    container.style.cursor = 'auto'
    container.style.WebkitTapHighlightColor = 'rgba(0, 0, 0, 0)'
  }

  _onVisibilityChange = () => {
    // Set the name of the hidden property and the change event for visibility
    let hidden, visibilityChange
    if (typeof document.hidden !== 'undefined') {
      // Opera 12.10 and Firefox 18 and later support
      hidden = 'hidden'
      visibilityChange = 'visibilitychange'
    } else if (typeof document.msHidden !== 'undefined') {
      hidden = 'msHidden'
      visibilityChange = 'msvisibilitychange'
    } else if (typeof document.webkitHidden !== 'undefined') {
      hidden = 'webkitHidden'
      visibilityChange = 'webkitvisibilitychange'
    }

    function handleVisibilityChange() {
      if (document[hidden]) {
        // TODO: pause ticker
        sound.muteAll()
      } else {
        // TODO: play ticker
        sound.unmuteAll()
      }
    }

    // Warn if the browser doesn't support addEventListener or the Page Visibility API
    if (
      typeof document.addEventListener === 'undefined' ||
      hidden === undefined
    ) {
      // eslint-disable-next-line
      console.log("Current browser doesn't support the Page Visibility API.")
    } else {
      // Handle page visibility change
      document.addEventListener(visibilityChange, handleVisibilityChange, false)
    }
  }

  _onResize = () => {
    const { width: designWidth, height: designHeight } = this.#options
    const { width: deviceWidth, height: deviceHeight } = Device.size.clone()

    const { scaleMode } = this.#options
    const scale = ScaleMode[scaleMode]
    if (!scale) {
      throw new Error(
        `[${classname(this)}] unsupported scale mode - ${scaleMode}`
      )
    }

    const {
      stage,
      viewportWidth,
      viewportHeight,
      viewportCSSWidth,
      viewportCSSHeight,
      shouldRotate,
    } = scale(designWidth, designHeight, deviceWidth, deviceHeight)

    this.#view.style.zIndex = Layer.VIEW
    this.#view.style.position = 'absolute'
    this.#view.style.width = `${viewportCSSWidth}px`
    this.#view.style.height = `${viewportCSSHeight}px`
    this.#renderer.resize(viewportWidth, viewportHeight)

    this.#stage.scale.set(stage.scale)
    if (shouldRotate) {
      this.#stage.rotation = 0.5 * Math.PI
      this.#stage.x = viewportWidth - stage.y
      this.#stage.y = stage.x
    } else {
      this.#stage.rotation = 0
      this.#stage.x = stage.x
      this.#stage.y = stage.y
    }

    this.stage = stage
  }
}

export default new FT()
